export type CrmContactRecommendationHintTone = "neutral" | "info" | "success" | "warning" | "danger";
export type CrmContactRecommendationHintPriority = "low" | "medium" | "high" | "critical";

export interface CrmContactRecommendationHint {
  key: string;
  label: string;
  category: string | null;
  summary: string;
  action: string | null;
  evidence: string | null;
  tone: CrmContactRecommendationHintTone;
  priority: CrmContactRecommendationHintPriority;
}

export interface CrmContactRecommendationHintSet {
  key: string;
  label: string;
  description: string | null;
  source: "configured";
  hints: CrmContactRecommendationHint[];
}

export interface BuildCrmContactRecommendationHintsOptions {
  hintKeys?: readonly string[];
  maxSets?: number;
  maxHintsPerSet?: number;
}

export const CRM_CONTACT_RECOMMENDATION_HINT_FIELD_KEYS = [
  "crmContactRecommendationHints",
  "contactRecommendationHints",
  "operatorHints",
  "salesHints",
] as const;

const PRIVATE_KEY_PATTERN = /(?:password|secret|token|authorization|credential|private[_-]?key|api[_-]?key)/i;

export function buildCrmContactRecommendationHints(
  customFields: unknown,
  options: BuildCrmContactRecommendationHintsOptions = {},
): CrmContactRecommendationHintSet[] {
  const fields = asRecord(customFields);
  if (!fields) return [];

  const hintKeys = options.hintKeys?.length
    ? [...options.hintKeys]
    : [...CRM_CONTACT_RECOMMENDATION_HINT_FIELD_KEYS];
  const maxSets = normalizePositiveInteger(options.maxSets, 6);
  const maxHintsPerSet = normalizePositiveInteger(options.maxHintsPerSet, 10);

  return hintKeys
    .flatMap((key) => parseConfiguredHintSets(key, fields[key], maxHintsPerSet))
    .filter((set) => set.hints.length > 0)
    .slice(0, maxSets);
}

function parseConfiguredHintSets(
  rootKey: string,
  value: unknown,
  maxHintsPerSet: number,
): CrmContactRecommendationHintSet[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parseHintSet(`${rootKey}.${index}`, entry, maxHintsPerSet))
      .filter((set): set is CrmContactRecommendationHintSet => Boolean(set));
  }

  const record = asRecord(value);
  if (!record) return [];

  if (hasSetShape(record)) {
    const set = parseHintSet(rootKey, record, maxHintsPerSet);
    return set ? [set] : [];
  }

  return Object.entries(record)
    .map(([key, entry]) => parseHintSet(key, entry, maxHintsPerSet))
    .filter((set): set is CrmContactRecommendationHintSet => Boolean(set));
}

function parseHintSet(
  fallbackKey: string,
  value: unknown,
  maxHintsPerSet: number,
): CrmContactRecommendationHintSet | null {
  const record = asRecord(value);
  if (!record) return null;

  const key = readString(record.key) ?? fallbackKey;
  if (isPrivateKey(key)) return null;

  const label = readString(record.label)
    ?? readString(record.title)
    ?? formatLabel(key);
  const description = readString(record.description) ?? readString(record.summary);
  const rawHints = record.hints ?? record.recommendations ?? record.items ?? record.actions ?? null;
  const hints = parseHints(rawHints ?? record, maxHintsPerSet);

  return {
    key,
    label,
    description,
    source: "configured",
    hints,
  };
}

function parseHints(value: unknown, maxHints: number): CrmContactRecommendationHint[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parseHint(`hint-${index + 1}`, entry))
      .filter((hint): hint is CrmContactRecommendationHint => Boolean(hint))
      .slice(0, maxHints);
  }

  const record = asRecord(value);
  if (!record) return [];

  const ignoredSetKeys = new Set(["key", "label", "title", "description", "summary", "hints", "recommendations", "items", "actions"]);
  return Object.entries(record)
    .filter(([key]) => !ignoredSetKeys.has(key))
    .map(([key, entry]) => parseHint(key, entry))
    .filter((hint): hint is CrmContactRecommendationHint => Boolean(hint))
    .slice(0, maxHints);
}

function parseHint(fallbackKey: string, value: unknown): CrmContactRecommendationHint | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  if (!record) return null;

  const key = readString(record.key) ?? fallbackKey;
  if (isPrivateKey(key)) return null;

  const summary = readString(record.summary)
    ?? readString(record.text)
    ?? readString(record.description)
    ?? readString(record.reason);
  if (!summary) return null;

  const label = readString(record.label)
    ?? readString(record.title)
    ?? formatLabel(key);

  return {
    key,
    label,
    category: readString(record.category) ?? readString(record.group),
    summary,
    action: readString(record.action) ?? readString(record.recommendedAction) ?? readString(record.nextStep),
    evidence: readString(record.evidence) ?? readString(record.source),
    tone: normalizeTone(readString(record.tone) ?? readString(record.status)),
    priority: normalizePriority(readString(record.priority) ?? readString(record.severity)),
  };
}

function hasSetShape(record: Record<string, unknown>): boolean {
  return ["label", "title", "description", "summary", "hints", "recommendations", "items", "actions"].some((key) => key in record);
}

function normalizeTone(value: string | null): CrmContactRecommendationHintTone {
  if (value === "info" || value === "success" || value === "warning" || value === "danger") return value;
  if (value === "critical" || value === "error" || value === "risk" || value === "blocked") return "danger";
  if (value === "ready" || value === "positive" || value === "opportunity") return "success";
  if (value === "review" || value === "hold" || value === "partial") return "warning";
  return "neutral";
}

function normalizePriority(value: string | null): CrmContactRecommendationHintPriority {
  if (value === "critical" || value === "high" || value === "medium" || value === "low") return value;
  if (value === "urgent" || value === "danger") return "critical";
  if (value === "warning" || value === "review") return "high";
  return "medium";
}

function normalizePositiveInteger(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : fallback;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function isPrivateKey(key: string): boolean {
  return PRIVATE_KEY_PATTERN.test(key);
}

function formatLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
